/**
 * PreviewDialog — always-on preview-before-commit dialog for VqeDdlEdit.
 *
 * Per Ralph/VqeDdlEdit/PRD.md § Slice H1 — Preview Dialog.
 *
 * The Visual DDL Editor's `commitDdlEdit` pipeline stages a `VqeDdlPendingEdit`
 * in `vqeDdlStore` before EVERY Designer / inline-DDL edit commit (always-on;
 * there is no byte threshold). This dialog is the global mount point that
 * renders the staged diff as red/green markup
 * (`<del>old</del><ins>new</ins>`) and gates commit on user confirmation.
 *
 * Two outcomes:
 *   - **Apply** — invokes the staged `apply()` thunk (writes to Monaco +
 *     re-syncs the resolver) and clears the staged state via `commitPending()`.
 *   - **Cancel** — clears the staged state via `cancelPending()` without
 *     touching Monaco. The buffer remains exactly as it was before the gate.
 *
 * The dialog is mounted once at the app shell so any in-flight Designer or
 * inline-toolbar edit funnels through the same UX without per-host plumbing.
 */
import { useCallback, useState } from 'react';
import { AlertCircle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useVqeDdlStore } from '../../store';

export const PREVIEW_DIALOG_TESTID = 'vqe-ddl-preview-dialog';
export const PREVIEW_DIALOG_DIFF_TESTID = 'vqe-ddl-preview-dialog-diff';
export const PREVIEW_DIALOG_APPLY_TESTID = 'vqe-ddl-preview-dialog-apply';
export const PREVIEW_DIALOG_CANCEL_TESTID = 'vqe-ddl-preview-dialog-cancel';

export function PreviewDialog() {
  const pendingEdit = useVqeDdlStore((s) => s.pendingEdit);
  const commitPending = useVqeDdlStore((s) => s.commitPending);
  const cancelPending = useVqeDdlStore((s) => s.cancelPending);
  const { track } = useAnalytics();
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = useCallback(async () => {
    if (!pendingEdit || applying) return;
    setApplying(true);
    setError(null);
    try {
      await pendingEdit.apply();
      track('feature:vqe_ddl_preview_decision', {
        decision: 'apply',
        op: pendingEdit.edit.op,
      });
      commitPending();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setApplying(false);
    }
  }, [pendingEdit, applying, track, commitPending]);

  const handleCancel = useCallback(() => {
    if (!pendingEdit || applying) return;
    track('feature:vqe_ddl_preview_decision', {
      decision: 'cancel',
      op: pendingEdit.edit.op,
    });
    cancelPending();
    setError(null);
  }, [pendingEdit, applying, track, cancelPending]);

  const open = pendingEdit !== null;
  const totalBytes = pendingEdit
    ? pendingEdit.preview.plannedEdits.reduce((sum, e) => sum + e.replacement.length, 0)
    : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleCancel();
      }}
    >
      <DialogContent
        className="max-w-2xl"
        data-testid={PREVIEW_DIALOG_TESTID}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Preview DDL change</DialogTitle>
          <DialogDescription>
            This change rewrites {totalBytes} byte{totalBytes === 1 ? '' : 's'} of
            SQL. Review the diff below, then Apply to commit or Cancel to drop it.
          </DialogDescription>
        </DialogHeader>

        <div
          className="max-h-[50vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words [&_del]:bg-red-500/15 [&_del]:text-red-700 dark:[&_del]:text-red-400 [&_del]:line-through [&_ins]:bg-green-500/15 [&_ins]:text-green-700 dark:[&_ins]:text-green-400 [&_ins]:no-underline"
          data-testid={PREVIEW_DIALOG_DIFF_TESTID}
          dangerouslySetInnerHTML={{ __html: pendingEdit?.preview.diffHtml ?? '' }}
        />

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span data-testid="vqe-ddl-preview-dialog-error">{error}</span>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={applying}
            data-testid={PREVIEW_DIALOG_CANCEL_TESTID}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleApply}
            disabled={applying}
            data-testid={PREVIEW_DIALOG_APPLY_TESTID}
          >
            {applying ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
